import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@lastrouter/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiMutation, useApiQuery } from '../api/hooks.js';
import { ErrorState, KeyValue, QueryState } from '../components/common.js';
import { splitList } from '../lib/format.js';

function Simulator() {
  const { t } = useTranslation();
  const sim = useApiMutation('simulateAccess');
  const [upn, setUpn] = useState('');
  const [roles, setRoles] = useState('');
  const [client, setClient] = useState('');

  function submit(e: FormEvent) {
    e.preventDefault();
    sim.mutate({ body: { upn: upn.trim(), roles: splitList(roles, /\n/), client_app: client.trim() || null } });
  }

  const r = sim.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('access.simTitle')}</CardTitle>
        <CardDescription>{t('access.simHelp')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form className="grid gap-3 md:grid-cols-3" onSubmit={submit}>
          <Field id="sim-upn" label={t('access.upn')}>
            <Input type="email" value={upn} onChange={(e) => setUpn(e.target.value)} required placeholder="maria@cliente.gov.br" />
          </Field>
          <Field id="sim-papeis" label={t('access.roles')}>
            <Textarea rows={2} value={roles} onChange={(e) => setRoles(e.target.value)} placeholder="Lastrouter.Leitor" />
          </Field>
          <Field id="sim-app" label={`${t('access.clientApp')} (${t('common.optional')})`}>
            <Input value={client} onChange={(e) => setClient(e.target.value)} />
          </Field>
          <div className="md:col-span-3">
            <Button type="submit" variant="primary" disabled={sim.isPending || !upn.trim()}>
              {t('access.simulate')}
            </Button>
          </div>
        </form>
        {sim.error ? <ErrorState error={sim.error} /> : null}
        {r ? (
          <div aria-live="polite" className="flex flex-col gap-3">
            {r.refused ? <Alert tone="warning" title={t('access.refused', { reason: r.refused })} /> : null}
            <KeyValue
              items={[
                { label: t('access.channel'), value: r.channel ?? '-' },
                { label: t('access.tools'), value: r.tools.length ? r.tools.map((x) => t(`channels.toolNames.${x}`)).join(', ') : t('common.none') },
                { label: t('access.subjects'), value: r.subjects.length ? r.subjects.map((s) => s.nome).join(', ') : t('common.none') },
                {
                  label: t('access.sourceUser'),
                  value: (
                    <ul>
                      {r.source_users.map((u) => (
                        <li key={u.source_id}>
                          <span className="font-mono">{u.user ?? '-'}</span> <span className="text-fg-muted">({u.source_id}: {u.detail})</span>
                        </li>
                      ))}
                    </ul>
                  ),
                },
              ]}
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Simular acesso (aba de Pessoas): os assuntos com os donos e o simulador de uma pessoa. */
export function AccessPanel() {
  const { t } = useTranslation();
  const q = useApiQuery('subjects');
  return (
    <div className="flex flex-col gap-6">
      <p className="text-fg-secondary">{t('access.subtitle')}</p>
      <Simulator />
      <QueryState query={q}>
        {(subjects) => (
          <Table stickyHeader>
            <TableCaption>{t('access.title')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{t('access.subject')}</TableHead>
                <TableHead>{t('access.source')}</TableHead>
                <TableHead>{t('access.owners')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subjects.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.nome}</TableCell>
                  <TableCell>{s.fonte}</TableCell>
                  <TableCell className="text-[length:var(--lr-text-sm)]">
                    {t('context.business')}: {s.donos.negocio}
                    <br />
                    {t('context.technical')}: {s.donos.tecnico}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </QueryState>
    </div>
  );
}
